import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { API } from '@unboared/react';

import { Game } from '~/types/game';
import { VoteContext } from '~/services/freePlay/types';
import { VoteService, castVote } from '~/services/freePlay';
import { useVoteTimer } from '~/hooks/freePlay';

import styles from './VoteOverlay.module.css';

export type VoteOverlayProps = {
    vote: VoteContext;
    proposedGame?: Game;
    /** When provided, called instead of API.emitAction (for debug/storybook). */
    onVote?: (choice: 'yes' | 'no') => void;
};

export function VoteOverlay({ vote, proposedGame, onVote }: VoteOverlayProps) {
    const { t } = useTranslation();
    const secondsLeft = useVoteTimer(vote.deadline);

    /* API.getDeviceID() throws when the session/device list isn't synced yet
       (gotcha #16) — e.g. a vote that starts right after a join/reconnect. Guard
       it so it can't crash the whole gamepad render; fall back to spectator view. */
    let myDeviceID = '__debug__';
    if (!onVote) {
        try {
            myDeviceID = API.getDeviceID();
        } catch {
            myDeviceID = '';
        }
    }
    const myVote = vote.votes[myDeviceID];
    /* Players who joined after the vote started are spectators — they can see the result but can't sway it. */
    const isSpectator = !vote.eligibleVoters.includes(myDeviceID);
    const tally = useMemo(() => VoteService.tally(vote), [vote]);
    const total = vote.eligibleVoters.length;
    const yesPct = total === 0 ? 0 : Math.round((tally.yes / total) * 100);

    let initiatorName = t('gamepad.freePlay.vote.aPlayer');
    try {
        const fetched = API.getUsername(vote.initiatorID);
        if (fetched) initiatorName = fetched;
    } catch {
        /* No active session (debug / storybook) — keep fallback. */
    }
    const proposedName = proposedGame?.name ?? vote.proposedGameID;
    const cover = proposedGame?.cover;

    const cast = (choice: 'yes' | 'no') => {
        if (onVote) onVote(choice);
        else castVote(choice);
    };

    const elapsed = Date.now() - vote.startedAt;
    const totalMs = vote.deadline - vote.startedAt;
    const ratio = Math.max(0, Math.min(1, 1 - elapsed / totalMs));
    const ringStyle = {
        background: `conic-gradient(var(--color-warning) ${ratio * 360}deg, var(--color-text-10) ${ratio * 360}deg)`,
    };

    return (
        <div className={styles.container}>
            <span className={styles.tag}>{t('gamepad.freePlay.vote.tag')}</span>
            <h2 className={styles.title}>
                {t('gamepad.freePlay.vote.title', { initiator: initiatorName, game: proposedName })}
            </h2>
            <p className={styles.subtitle}>
                {total} {t('gamepad.freePlay.players')} · {t('gamepad.freePlay.vote.majority')}
            </p>

            <div className={styles.preview} style={cover ? { backgroundImage: `url(${cover})` } : undefined}>
                <div className={styles.previewContent}>
                    <h3 className={styles.previewName}>{proposedName}</h3>
                    {proposedGame && (
                        <div className={styles.previewMeta}>
                            {proposedGame.numberOfPlayers} · {proposedGame.playTime}
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.timerRing} style={ringStyle}>
                <span className={styles.timerValue}>{secondsLeft}</span>
            </div>

            {(() => {
                if (isSpectator) {
                    return (
                        <button type="button" className={styles.btnVoted} disabled>
                            {t('gamepad.freePlay.vote.spectator')}
                        </button>
                    );
                }
                if (myVote) {
                    return (
                        <button type="button" className={styles.btnVoted} disabled>
                            {myVote === 'yes'
                                ? `✓ ${t('gamepad.freePlay.vote.votedYes')}`
                                : `✕ ${t('gamepad.freePlay.vote.votedNo')}`}
                        </button>
                    );
                }
                return (
                    <div className={styles.actions}>
                        <button type="button" className={styles.btnYes} onClick={() => cast('yes')}>
                            ✓ {t('gamepad.freePlay.vote.yes')}
                        </button>
                        <button type="button" className={styles.btnNo} onClick={() => cast('no')}>
                            ✕ {t('gamepad.freePlay.vote.no')}
                        </button>
                    </div>
                );
            })()}

            <div className={styles.tallyBar}>
                <div className={styles.tallyFill} style={{ width: `${yesPct}%` }} />
            </div>
            <div className={styles.tallyText}>
                <span>
                    <strong>{tally.yes}</strong> {t('gamepad.freePlay.vote.yesLabel')}
                </span>
                <span>
                    {tally.no} {t('gamepad.freePlay.vote.noLabel')} · {tally.pending}{' '}
                    {t('gamepad.freePlay.vote.pendingLabel')}
                </span>
            </div>
        </div>
    );
}
